import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { X, Check } from "lucide-react";
import { mediaUrl, setPoster } from "../../lib/api";
import { captureFrameFromEl } from "../../lib/videoFrame";

const fmt = (s) => {
  if (!s || !isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}:${String(r).padStart(2, "0")}`;
};

export default function FramePicker({ video, onClose, onSaved }) {
  const ref = useRef(null);
  const [dur, setDur] = useState(0);
  const [time, setTime] = useState(0);
  const [saving, setSaving] = useState(false);

  const onLoaded = () => {
    const v = ref.current;
    if (v) setDur(v.duration || 0);
  };
  const onSeek = (e) => {
    const t = parseFloat(e.target.value);
    setTime(t);
    if (ref.current) ref.current.currentTime = t;
  };

  const useFrame = async () => {
    const v = ref.current;
    if (!v) return;
    setSaving(true);
    try {
      const blob = await captureFrameFromEl(v);
      await setPoster(video.id, blob);
      toast.success("Thumbnail updated");
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error("Could not capture this frame");
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div data-testid="frame-picker" className="fixed inset-0 z-[10001] bg-black/80 backdrop-blur-sm flex items-center justify-center px-4"
      onClick={onClose}>
      <div className="w-full max-w-2xl bg-[var(--vsl-bg)] border border-[var(--vsl-border)]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--vsl-border)]">
          <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-[var(--vsl-muted)]">Choose thumbnail frame</p>
          <button data-testid="frame-picker-close" onClick={onClose} className="text-[var(--vsl-muted)] hover:text-[var(--vsl-fg)]"><X size={18} /></button>
        </div>
        <div className="p-4 space-y-4">
          <div className="bg-[#141312] flex items-center justify-center max-h-[55vh] overflow-hidden">
            <video ref={ref} src={mediaUrl(video.url)} muted playsInline preload="auto"
              onLoadedMetadata={onLoaded} onLoadedData={onLoaded}
              className="max-h-[55vh] w-auto" />
          </div>
          <div className="flex items-center gap-3">
            <span className="font-mono text-[10px] text-[var(--vsl-muted)] w-10 tabular-nums">{fmt(time)}</span>
            <input data-testid="frame-slider" type="range" min={0} max={dur || 0} step={0.05} value={time} onChange={onSeek}
              className="flex-1 accent-[var(--vsl-limestone)]" />
            <span className="font-mono text-[10px] text-[var(--vsl-muted)] w-10 tabular-nums text-right">{fmt(dur)}</span>
          </div>
          <div className="flex items-center justify-end gap-3">
            <button onClick={onClose} className="font-mono text-[10px] tracking-[0.2em] uppercase text-[var(--vsl-muted)] hover:text-[var(--vsl-fg)] px-3 py-2">Cancel</button>
            <button data-testid="use-frame-btn" onClick={useFrame} disabled={saving}
              className="bg-[var(--vsl-fg)] text-[var(--vsl-bg)] px-4 py-2 font-mono text-[10px] uppercase tracking-wider inline-flex items-center gap-2 hover:bg-[var(--vsl-limestone)] transition-colors disabled:opacity-50">
              <Check size={13} /> {saving ? "Saving…" : "Use this frame"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
